import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../db';
import { db as fdb } from '../firebase';
import { keyOpensRecord, makeVaultKey, unlockVaultKey, type VaultKeyRecord } from './vaultKey';

/**
 * Whether this account's synced notes are encrypted, and whether this device
 * can read them.
 *
 * Two pieces live in two places, on purpose:
 *
 *  - the **record** (salt + verifier) is stored with the account, so a second
 *    device can tell a right passphrase from a wrong one without the server
 *    ever seeing either;
 *  - the **key** is stored on the device as a non-extractable `CryptoKey`, so a
 *    reload does not ask again and the passphrase is never written down.
 *
 * A locked device — the vault is encrypted, this device has no key yet — is a
 * state, not an error: it keeps its local notes, refuses to push them (which
 * would put plaintext into an encrypted vault), and leaves incoming records
 * alone until somebody types the passphrase.
 */

const KEY_STORE = 'vaultKey';
const RECORD_CACHE = 'vaultRecord';

export type VaultStatus =
  /** Sync stores documents as text (the old behaviour). */
  | { state: 'off' }
  /** Encrypted, and this device can read it. */
  | { state: 'unlocked' }
  /** Encrypted, and this device cannot read it yet. */
  | { state: 'locked' };

let cachedKey: CryptoKey | null = null;

function vaultRef(uid: string) {
  return doc(fdb, 'vaults', uid);
}

/** The account's key record, from the server, or the last one seen while offline. */
export async function loadVaultRecord(uid: string): Promise<VaultKeyRecord | null> {
  try {
    const snapshot = await getDoc(vaultRef(uid));
    const remote = snapshot.exists() ? (snapshot.data() as VaultKeyRecord) : null;
    await db.kv.put({ key: RECORD_CACHE, value: remote });
    return remote;
  } catch {
    // Offline: the cached record still tells this device that the vault is
    // encrypted, which is what stops it pushing plaintext.
    return ((await db.kv.get(RECORD_CACHE))?.value as VaultKeyRecord | null) ?? null;
  }
}

async function storedKey(): Promise<CryptoKey | null> {
  if (cachedKey) return cachedKey;
  const stored = (await db.kv.get(KEY_STORE))?.value;
  cachedKey = stored instanceof CryptoKey ? stored : null;
  return cachedKey;
}

async function keepKey(key: CryptoKey | null): Promise<void> {
  cachedKey = key;
  if (key) await db.kv.put({ key: KEY_STORE, value: key });
  else await db.kv.delete(KEY_STORE);
}

/** The key for encrypting and decrypting, or null when there is none here. */
export function vaultKey(): CryptoKey | null {
  return cachedKey;
}

/** Load what this device knows, and say where it stands. */
export async function vaultStatus(uid: string | null): Promise<VaultStatus> {
  if (!uid) return { state: 'off' };
  const record = await loadVaultRecord(uid);
  if (!record) {
    await keepKey(null);
    return { state: 'off' };
  }
  const key = await storedKey();
  // A key left behind by another account on this device is not this vault's
  // key: check it against the record rather than trusting its presence.
  if (key && (await keyOpensRecord(key, record))) return { state: 'unlocked' };
  if (key) await keepKey(null);
  return { state: 'locked' };
}

/**
 * Start encrypting this account's synced notes.
 *
 * Refuses if the account already has a vault: that would strand every document
 * already stored under the old key. Changing the passphrase is a different
 * operation, and not one to do by accident.
 */
export async function enableVault(uid: string, passphrase: string): Promise<boolean> {
  if (await loadVaultRecord(uid)) return false;
  const { key, record } = await makeVaultKey(passphrase);
  await setDoc(vaultRef(uid), record);
  await db.kv.put({ key: RECORD_CACHE, value: record });
  await keepKey(key);
  return true;
}

/** Try a passphrase on this device. False when it is the wrong one. */
export async function unlockVault(uid: string, passphrase: string): Promise<boolean> {
  const record = await loadVaultRecord(uid);
  if (!record) return false;
  const key = await unlockVaultKey(passphrase, record);
  if (!key) return false;
  await keepKey(key);
  return true;
}

/**
 * Stop encrypting: the record goes, and the documents are re-uploaded as text
 * by the caller (which is why this needs the key to still be here).
 */
export async function disableVault(uid: string): Promise<boolean> {
  if (!(await storedKey())) return false;
  await deleteDoc(vaultRef(uid)).catch(() => {});
  await db.kv.put({ key: RECORD_CACHE, value: null });
  await keepKey(null);
  return true;
}

/** Forget the key on this device only (sign-out). The vault stays encrypted. */
export async function forgetVaultKey(): Promise<void> {
  await keepKey(null);
  await db.kv.delete(RECORD_CACHE);
}
